import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmSignIn } from '../api.js';
import { AuthLayout } from '../components/AuthLayout.js';
import { Brand } from '../components/Brand.js';
import { safeReturnPath } from '../return-path.js';

// POST-to-confirm sign-in (auth hardening). The magic link is opened here: the api's GET /auth/verify
// validates the link and redirects it to this page WITHOUT consuming the token. We require an explicit
// click to POST the token — so a mail scanner's passive pre-fetch (HEAD or GET) of the emailed link can
// never spend it. On success the session cookies are set server-side and we enter the app; on failure
// we return to sign-in with the reason so the user can request a fresh link.
export function ConfirmSignIn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [submitting, setSubmitting] = useState(false);

  async function onConfirm() {
    setSubmitting(true);
    const result = await confirmSignIn(token);
    if (result.ok) {
      // FINISH THE JOURNEY THEY STARTED (#968). This used to be a hard-coded '/', so a shared deep
      // link opened on a lapsed session ended at the project list — the document, the record and the
      // reason someone sent it all gone, with nothing saying a link had been dropped.
      //
      // 🔴 The destination is checked HERE, immediately before it is used, even though the api
      // filtered it at mint and again on the way out. This is the only place a navigation happens and
      // the only place that knows this origin; an unvalidated redirect on the sign-in route is an
      // open redirect, which is a worse defect than the one being fixed. Anything that does not
      // survive falls back to '/' — the behaviour before any of this existed.
      const destination = safeReturnPath(result.returnPath, window.location.origin) ?? '/';
      // Full-page navigation so the app shell boots and reads the freshly-set session cookie.
      window.location.assign(destination);
      return;
    }
    setSubmitting(false);
    void navigate(`/sign-in?error=${result.reason ?? 'link_invalid'}`, { replace: true });
  }

  return (
    <AuthLayout>
      <Brand />
      {token ? (
        <>
          <h1>Confirm your sign-in</h1>
          <p className="sub">
            One more tap for your security &mdash; confirm it&rsquo;s really you finishing sign-in
            on this device.
          </p>
          <button type="button" disabled={submitting} onClick={() => void onConfirm()}>
            {submitting ? 'Signing you in…' : 'Confirm sign-in'}
          </button>
        </>
      ) : (
        <>
          <h1>This sign-in link is incomplete</h1>
          <p className="sub">Request a fresh link to continue.</p>
          <button
            type="button"
            className="link-btn"
            onClick={() => void navigate('/sign-in', { replace: true })}
          >
            Back to sign in
          </button>
        </>
      )}
    </AuthLayout>
  );
}
